import { TmsContext } from '@awazevr/mfe-common.contexts.tms'
import { MutableRefObject, useContext, useEffect, useRef } from 'react'
import selectors from './selectors'

const useDatalayerOnClick = (): {
  ref: MutableRefObject<HTMLDivElement | null>
} => {
  const tms = useContext(TmsContext)
  const ref = useRef<HTMLDivElement | null>(null)
  function handleClick(e: Event) {
    const elementsToTrack = [
      {
        selector: selectors.searchButtonElement,
        updateDataLayer: (element) => {
          const guests =
            RegExp(/\d+/).exec(
              ref.current.querySelector("[data-ga='party-selector']")
                ?.textContent,
            )?.[0] || '0'
          const clearSearchButton =
            element.getAttribute('data-ga') ===
            'autocomplete-clear-search-input'
          tms?.updateDataLayer(
            'link',
            clearSearchButton ? 'search-clear' : 'search-section start',
            !clearSearchButton && { search_option_section: guests },
          )
        },
      },
      {
        selector: selectors.mobileBurgerMenuIcon,
        updateDataLayer: (element) => {
          const open =
            element.getAttribute('data-ga') === 'burger-menu-button-open'
          tms?.updateDataLayer('link', `menu-${open ? 'open' : 'close'}`)
        },
      },
      {
        selector: selectors.navigationElement,
        updateDataLayer: (element) => {
          const [, linkText = '', linkUrl = '', linkPosition = ''] =
            element.getAttribute('data-ga')?.split('-$-') || []
          tms?.updateDataLayer('link', 'menu-link', {
            link_text: linkText.trim(),
            link_url: linkUrl,
            link_position: linkPosition,
          })
        },
      },
      {
        selector: selectors.accordionTitle,
        updateDataLayer: (element) => {
          const open = element.hasAttribute('open')

          tms?.updateDataLayer(
            'link',
            `accordion-${!open ? 'open' : 'close'}`,
            {
              accordion_header: element.textContent?.trim(),
            },
          )
        },
      },
      {
        selector: selectors.accordionLink,
        updateDataLayer: (element) => {
          const index = Array.from(element.parentNode?.children).indexOf(
            element,
          )

          tms?.updateDataLayer('link', 'accordion-link', {
            accordion_header: element
              .closest('[data-testid="accordion-item"]')
              .querySelector('[data-ga="accordion-title"]').textContent,
            accordion_link_text: element.textContent?.trim(),
            accordion_link_position: index + 1,
            accordion_link_url: element.getAttribute('href'),
          })
        },
      },
      {
        selector: selectors.socialMediaIcon,
        updateDataLayer: (element) => {
          tms?.updateDataLayer('link', 'outbound-link', {
            link_type: 'social media',
            link_url: element.getAttribute('href'),
          })
        },
      },
    ] satisfies {
      selector: string
      updateDataLayer: (element: HTMLElement) => void
    }[]

    for (const { updateDataLayer, selector } of elementsToTrack) {
      const element: HTMLElement | null = (e.target as HTMLElement)?.closest(
        selector,
      )

      if (element) {
        updateDataLayer(element)
        break
      }
    }
  }

  useEffect(() => {
    const currentElement = ref.current

    if (currentElement) {
      currentElement.addEventListener('click', handleClick)

      // Cleanup: remove the event listener when the component unmounts
      return () => {
        currentElement.removeEventListener('click', handleClick)
      }
    }
  }, [])

  return { ref }
}

export default useDatalayerOnClick
